import { useRef, useState, type ReactNode } from 'react'
import { messageOf } from '../lib/errors'
import { stampAskOne } from '../lib/askOneDesk'
import { seededPageContext } from '../lib/helpLink'
import {
  askIntent,
  intentRouting,
  postQuestion,
  type AskIntent,
  type AskIntentKey,
  type BoardPost,
} from '../lib/wiki'
import {
  ASKER_PRIORITIES,
  createSupportQuestion,
  headlineAndDetail,
  laneWords,
  requestSupportTranslation,
  sendSupportFeedback,
  type AskerPriority,
  type SupportLane,
} from '../lib/support'
import { useHubLang } from '../lib/i18n'
import { ASK, HELP, intentText, type Emph as EmphParts } from '../lib/hubCopy'
import {
  MAX_SUPPORT_PICTURES,
  attachSupportPictures,
  capturePagePicture,
  prepareSupportPicture,
  readPageContext,
  type PageContext,
  type SupportPicture,
} from '../lib/supportMedia'

/**
 * REACHING A PERSON — the forms and the one send behind them.
 *
 * ── WHY THIS FILE EXISTS (31 Oct, HLP-44; One and Help v2 S2) ──────────────
 *
 * Until S2 all of this lived in AskOneDrawer.tsx, because the drawer was where
 * a question became a ticket: intents before the answer, a routing step after
 * a thumbs-down, a one-tap send for an idea or a fault. v2 splits the jobs.
 * Ask One answers and sends NOTHING; reaching a person is #/help, where the
 * person opens the form themselves (D1-D3). So the forms moved out of the
 * drawer, whole, rather than being copied into Help: one `useRouting`, one
 * `RouteQuestionFields`, one `NoPrivateHrRoute` - the same rule (11) that put
 * them in the drawer file in the first place, now in a file named for them.
 *
 * Callers today: HelpPage (the ticket and feedback dialogs) and the #/wiki
 * Ask One panel, which keeps ONE door here - "Post to the board", the page's
 * own button. The community board is a public post, not help, and it stays on
 * Wiki One only (v2's "what moves where").
 *
 * THE HR HOLD IS UNCHANGED. `intentRouting` (lib/wiki.ts) still returns
 * 'hold' for HR first and unconditionally, and 121's `v_to = 'hr'` is still
 * asserted beside it: nothing here can send to HR, whatever a link says.
 */

/** A sentence with a bold phrase in it — lib/hubCopy.ts keeps them as three
 *  plain parts so the Spanish reader sees text, not markup. */
export function Emph({ parts }: { parts: EmphParts }) {
  return (
    <>
      {parts[0]}
      <strong>{parts[1]}</strong>
      {parts[2]}
    </>
  )
}

/**
 * THE DESTINATIONS, AS A LIST THAT SAYS WHO SEES EACH ONE — extracted from
 * RouteStep on 31 Oct (337, HLP-43) so Help's ticket dialog shows the SAME
 * rows (rule 11): the label is where it goes, the blurb is who reads it.
 * With `selected` it is a choice that stays on screen (the ticket dialog,
 * where the person picks first — D2). RouteStep, its other caller, went with
 * the drawer's routing step in S2 (31 Oct).
 */
export function IntentChoices({
  intents,
  onPick,
  selected,
}: {
  intents: ReadonlyArray<AskIntent>
  onPick: (key: AskIntentKey) => void
  selected?: AskIntentKey | null
}) {
  const lang = useHubLang()
  return (
    <ul className="oneintent__list">
      {intents.map((i) => (
        <li key={i.key}>
          <button
            type="button"
            className={`oneintent__btn${selected === i.key ? ' oneintent__btn--on' : ''}`}
            aria-pressed={selected === undefined ? undefined : selected === i.key}
            onClick={() => onPick(i.key)}
          >
            <span className="oneintent__name">{intentText(lang, i).label}</span>
            {/* WHO ELSE SEES IT, on every row without exception. The seam
                this design chose is audience rather than subject (HLP-1),
                and a row that names a team without saying who reads it is
                asking somebody to sort themselves by subject matter — which
                16 Sep established they do not do. */}
            <span className="oneintent__who">{intentText(lang, i).blurb}</span>
          </button>
        </li>
      ))}
    </ul>
  )
}

/**
 * 348 (HLP-43, D4). "How much is this in your way?" — Mockup C's four answers,
 * each with its plain-words hint. NOTHING IS PICKED until the person picks:
 * a default would be the system answering for them, and the desk orders Needs
 * reply by this (can't work first). Radios, so a keyboard moves between them
 * and a screen reader hears one question with four answers.
 */
export function PriorityChoices({
  value,
  onPick,
}: {
  value: AskerPriority | null
  onPick: (p: AskerPriority) => void
}) {
  const t = HELP[useHubLang()]
  return (
    <fieldset className="onepriority">
      <legend className="oneroute__label">{t.priorityLabel}</legend>
      {ASKER_PRIORITIES.map((p) => (
        <label key={p} className={`onepriority__opt${value === p ? ' onepriority__opt--on' : ''}`}>
          <input
            type="radio"
            name="onepriority"
            value={p}
            checked={value === p}
            onChange={() => onPick(p)}
          />
          <span className="onepriority__name">{t.priorities[p].label}</span>
          <span className="onepriority__hint">{t.priorities[p].hint}</span>
        </label>
      ))}
    </fieldset>
  )
}

/**
 * WHAT IS SENT, AND WHO WILL SEE IT — extracted from RouteStep on 31 Oct (337,
 * HLP-43) so the routing step and Help's ticket dialog are one form, not two.
 * `toLane` null is the PUBLIC board (BoardStep, #/wiki only); a lane is a
 * private send (Help's ticket).
 */
export function RouteQuestionFields({
  question,
  setQuestion,
  detail,
  setDetail,
  toLane,
  media,
  priority,
}: {
  question: string
  setQuestion: (v: string) => void
  detail: string
  setDetail: (v: string) => void
  toLane: SupportLane | null
  media?: RouteMedia
  /** 348. Help's ticket: the choice sits after the message, before pictures
   *  (Mockup C's order). Absent on the board, which has no lane to tell. */
  priority?: { value: AskerPriority | null; onPick: (p: AskerPriority) => void }
}) {
  const lang = useHubLang()
  const c = ASK[lang]
  return (
    <>
      {/* The exact text that will publish, editable. Not a preview of a
          paraphrase — the row that gets written is this. */}
      <label className="oneroute__label" htmlFor="oneroute-q">
        {c.questionLabel}
      </label>
      <input
        id="oneroute-q"
        className="composer__title"
        maxLength={300}
        value={question}
        onChange={(e) => setQuestion(e.target.value)}
        placeholder={c.questionPlaceholder}
      />

      <label className="oneroute__label" htmlFor="oneroute-d">
        {c.detailLabel}
      </label>
      {/* ONE'S SUMMARY TRAVELS WITH THE VERBATIM QUESTION, NEVER INSTEAD OF IT,
          and it is editable before it sends. A curator acting on a machine
          paraphrase of an employee's own words, with neither able to see what
          was actually typed, is a failure mode this design would be
          introducing from nothing. */}
      <textarea
        id="oneroute-d"
        className="composer__body"
        rows={4}
        maxLength={2000}
        value={detail}
        onChange={(e) => setDetail(e.target.value)}
        placeholder={c.detailPlaceholder}
      />

      {/* 034'S WARNING, AND IT ONLY BELONGS ON THE PUBLIC DOOR. Wiki One
          questions are public and carry the asker's name. It used to end "a
          curator reads it first and nothing appears until they publish it";
          096 (David, 22 Sep) replaced the curator gate with the same content
          check a post gets, so the sentence says THAT — and says what happens
          when the check says no, because a question that silently fails to
          appear is the "big brothery" feeling the change exists to remove.
          Putting it over a PRIVATE send would be the opposite fault: a true
          sentence about the wrong destination reads as a warning about this
          one. */}
      {!toLane && (
        <p className="oneroute__warn">
          <Emph parts={c.publicWarn} />
        </p>
      )}
      {/* PRIVATE MEANS PRIVATE TO A NAMED AUDIENCE, NOT ANONYMOUS (16 Sep).
          Pseudonymity does not survive 57 BUs — B15's k=6 — so the asker is
          named to the receiving team and this says so before they send. It
          claims nothing about a reply time, because nothing here can keep
          that promise. */}
      {toLane && priority && <PriorityChoices value={priority.value} onPick={priority.onPick} />}

      {toLane && media && <SupportPicturesField media={media} lane={toLane} />}

      {toLane && (
        <p className="oneroute__warn">
          <Emph parts={c.laneWarn(laneWords(toLane, lang))} />
        </p>
      )}
    </>
  )
}

/**
 * PICTURES AND WHERE YOU WERE, ON A PRIVATE QUESTION (116, HLP-26; David and
 * Chandra, 25 Sep).
 *
 * Chandra's reports are mostly "not what I expected", and every one arrived as
 * a screenshot in Teams. So a private question can carry up to three pictures:
 * one the person CHOOSES (first, because it is the one that always works — on
 * a phone it opens the photo picker, where the screenshot already is) or a
 * picture of the page the app DRAWS itself (platform lane only; drawn from the
 * page's markup, so video comes out blank, which is why it is a thumbnail with
 * Remove before anything sends).
 *
 * THE PAGE DETAILS ARE SAID BEFORE THEY GO, VALUES AND ALL. Attaching the
 * screen, window, app version, browser and language automatically is David's
 * call, and the condition on it is that nobody finds out afterwards: the
 * sentence names the actual screen and window rather than "some technical
 * details". Platform lane only — a question about field data is not about the
 * screen it was typed on.
 *
 * Both go ONLY to the question's two readers (116's bucket and trigger), and
 * the sentence under the pictures says so.
 */
export function SupportPicturesField({ media, lane }: { media: RouteMedia; lane: SupportLane }) {
  const c = ASK[useHubLang()]
  const full = media.pictures.length >= MAX_SUPPORT_PICTURES
  const platform = lane === 'platform'
  return (
    <div className="onepics">
      <p className="oneroute__label">{c.picturesLabel}</p>
      {media.pictures.length > 0 && (
        <ul className="onepics__list">
          {media.pictures.map((p, i) => (
            <li className="onepics__item" key={p.previewUrl}>
              <img className="onepics__thumb" src={p.previewUrl} alt={p.fromPage ? c.pagePictureTag : ''} />
              {p.fromPage && <span className="onepics__tag">{c.pagePictureTag}</span>}
              <button type="button" className="btn btn--quiet btn--xs" onClick={() => media.remove(i)}>
                {c.removePicture}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="onepics__actions">
        {/* A LABEL around a hidden file input, so the whole control is the
            platform's own picker: the camera roll on a phone, a file dialog on
            a laptop. Nothing about the file is read until it is chosen. */}
        <label className="btn btn--sm" aria-disabled={full || media.busy}>
          {c.attachPicture}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            className="sr-only"
            disabled={full || media.busy}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? [])
              e.target.value = ''
              void media.addFiles(files)
            }}
          />
        </label>
        {platform && (
          <button
            type="button"
            className="btn btn--sm"
            disabled={full || media.busy}
            onClick={() => void media.capture()}
          >
            {media.busy ? c.capturing : c.capturePage}
          </button>
        )}
      </div>
      <p className="onepics__hint">{c.picturesHint(MAX_SUPPORT_PICTURES)}</p>
      {media.error && <p className="onedrawer__error" role="alert">{media.error}</p>}
      {platform && media.pageContext && (
        <p className="onepics__hint">
          {c.pageDetailsNote(media.pageContext.route, media.pageContext.viewport, media.pageContext.app_version)}
        </p>
      )}
    </div>
  )
}

/** What the routing step needs to show and change the pictures (116). */
export type RouteMedia = {
  pictures: SupportPicture[]
  pageContext: PageContext | null
  busy: boolean
  error: string | null
  addFiles: (files: File[]) => Promise<void>
  capture: () => Promise<void>
  remove: (index: number) => void
}

/* ── the routing, owned once ─────────────────────────────────────────────── */

/**
 * EVERY SEND TO A PERSON, IN ONE HOOK (22 Sep; reshaped 31 Oct, S2).
 *
 * The send is the part that must never exist twice: which write path, which
 * lane, what the confirmation says. A copy per surface is how the public
 * warning once lived in a screen that did not publish (WIK-19's whole reason).
 * Since S2 its holders are Help's two dialogs (`start`, `post`,
 * `sendFeedback`) and #/wiki's board button (`openBoard`, `post`). Ask One
 * holds none of it: nothing leaves One without a form the person opened.
 *
 * The door is chosen HERE (`routeKey`), by the person, in the form.
 */
export type RouteOutcome =
  | { kind: 'lane'; lane: SupportLane; picturesFailed?: number }
  | { kind: 'board'; post: BoardPost }
  /** 337. Feedback from Help: always the platform desk (D3). */
  | { kind: 'feedback' }

export function useRouting(openLanes?: readonly SupportLane[]) {
  const lang = useHubLang()
  const [phase, setPhase] = useState<'idle' | 'routing' | 'sent'>('idle')
  const [question, setQuestion] = useState('')
  const [detail, setDetail] = useState('')
  /**
   * What One actually said, kept apart from `detail` so the lane receives two
   * fields rather than one blob (17 Sep). `detail` is the person's words and
   * is theirs to edit; this is the machine's, and it is what the team checks
   * the paraphrase against. It is never sent on the PUBLIC path — a wiki
   * question has no column for it and 037 is why it must not gain one.
   */
  const [oneSummary, setOneSummary] = useState('')
  const [routeKey, setRouteKey] = useState<AskIntentKey | null>(null)
  /** 348 (D4). Null until the person picks; a ticket does not send without it.
   *  A prefilled link from One never sets it - the person chooses. */
  const [priority, setPriority] = useState<AskerPriority | null>(null)
  const [posting, setPosting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<RouteOutcome | null>(null)
  // 116: pictures and where the person was. Read when the step opens.
  const [pictures, setPictures] = useState<SupportPicture[]>([])
  const [pageContext, setPageContext] = useState<PageContext | null>(null)
  /* 126. The conversation's log row, handed in by `escalate`, so the send can
     stamp WHERE THE PERSON WENT (`stampAskOne`). Memory only, dropped on reset:
     HLP-13's transcript must never hold it. The INTENT is not stamped here —
     the drawer stamps that at the first answer, from what was declared, and a
     door pre-selected by the page is not a declaration. */
  const logRefRef = useRef<string | null>(null)
  const [mediaBusy, setMediaBusy] = useState(false)
  const [mediaError, setMediaError] = useState<string | null>(null)

  function clearPictures() {
    for (const p of pictures) URL.revokeObjectURL(p.previewUrl)
    setPictures([])
    setMediaError(null)
  }

  async function addPictures(files: File[], fromPage: boolean) {
    setMediaError(null)
    setMediaBusy(true)
    try {
      const room = MAX_SUPPORT_PICTURES - pictures.length
      const ready: SupportPicture[] = []
      for (const f of files.slice(0, Math.max(0, room))) {
        ready.push(await prepareSupportPicture(f, fromPage))
      }
      setPictures((cur) => [...cur, ...ready].slice(0, MAX_SUPPORT_PICTURES))
    } catch {
      setMediaError(ASK[lang].pictureFailed)
    } finally {
      setMediaBusy(false)
    }
  }

  const media: RouteMedia = {
    pictures,
    pageContext,
    busy: mediaBusy,
    error: mediaError,
    addFiles: (files) => addPictures(files, false),
    capture: async () => {
      try {
        const file = await capturePagePicture()
        await addPictures([file], true)
      } catch {
        setMediaError(ASK[lang].pictureFailed)
      }
    },
    remove: (i) =>
      setPictures((cur) => {
        const gone = cur[i]
        if (gone) URL.revokeObjectURL(gone.previewUrl)
        return cur.filter((_, j) => j !== i)
      }),
  }

  /** #/wiki's "Post to the board" (S2: the only door still opened from an
   *  answer, and only by the page's own button). Seeds the form from what was
   *  actually asked - the verbatim question, and One's words as background.
   *  Both editable before anything publishes. */
  function openBoard(fromQuestion: string, summary: string, logRef?: string | null) {
    logRefRef.current = logRef ?? null
    setQuestion(fromQuestion.slice(0, 300))
    // In the ASKER's language: this text is sent, and it sits under their name.
    setDetail(summary ? ASK[lang].escalateDetail(summary) : ASK[lang].escalateNoAnswer)
    setOneSummary(summary)
    setRouteKey('board')
    setError(null)
    setPageContext(readPageContext(`${lang} · ${typeof navigator !== 'undefined' ? navigator.language : ''}`))
    setPhase('routing')
  }

  /**
   * 337 (HLP-43). Help's ticket dialog, opened by the person. The lane is
   * pre-selected (D2: Platform support) and theirs to change. The page details
   * are read now, and shown before they go (116).
   *
   * S2 (31 Oct): `seed` is a prefilled link's words - One's "Need a person?"
   * or an idea/fault it recognised (HLP-41, kept as a link). They fill the
   * form and are theirs to edit; nothing sends until they press Send. `from`
   * is the conversation's log row, so the send still stamps where the person
   * went (126) once they DO send.
   */
  function start(hint: AskIntentKey, seed?: { text?: string; from?: string | null; page?: string | null }) {
    logRefRef.current = seed?.from ?? null
    const words = (seed?.text ?? '').trim()
    const { body, detail: rest } = words ? headlineAndDetail(words) : { body: '', detail: undefined }
    setQuestion(body)
    setDetail(rest ?? '')
    setOneSummary('')
    setRouteKey(hint)
    setPriority(null)
    setError(null)
    setOutcome(null)
    // S3 (31 Oct): a link from One carries the page the person was ON, so the
    // details name it rather than #/help (seededPageContext re-validates).
    setPageContext(
      seededPageContext(
        readPageContext(`${lang} · ${typeof navigator !== 'undefined' ? navigator.language : ''}`),
        seed?.page,
      ),
    )
    setPhase('routing')
  }

  /**
   * 337 (D3). Feedback from Help. The platform desk only, and only when
   * `intentRouting` says that lane really sends - a door that reaches nobody
   * is worse than no door. The server writes the lane; nothing here can send
   * feedback anywhere else.
   */
  async function sendFeedback(text: string, from?: string | null) {
    if (!text.trim() || posting) return
    if (intentRouting('platform', openLanes) !== 'lane') return
    setPosting(true)
    setError(null)
    try {
      const id = await sendSupportFeedback({ text })
      void requestSupportTranslation(id)
      void stampAskOne(from ?? null, { outcome: 'platform' })
      setOutcome({ kind: 'feedback' })
      setPhase('sent')
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setPosting(false)
    }
  }

  /**
   * THE SEND, AND WHICH ONE IT IS COMES FROM `intentRouting` — the same
   * function the card rendered from, called with the same arguments. A second
   * test here ("is there a lane?") would be a second answer to which door this
   * is, and the two would disagree exactly on the send, after the person has
   * read a card that said something else. `hold` sends NOTHING, whatever
   * called this.
   */
  async function post() {
    if (!question.trim()) return
    const routing = intentRouting(routeKey, openLanes)
    if (routing === 'hold') return
    const lane = routing === 'lane' ? askIntent(routeKey)?.lane ?? null : null
    // 348. A ticket is not sent without the asker's answer; the button says so.
    if (lane && !priority) return
    setPosting(true)
    setError(null)
    try {
      if (lane && priority) {
        const id = await createSupportQuestion({
          lane,
          askerPriority: priority,
          body: question,
          detail: detail || undefined,
          oneSummary: oneSummary || undefined,
          // Platform only — the lane the page is the subject of. The routing
          // step said so, with the values, before this ran.
          pageContext: lane === 'platform' ? pageContext ?? undefined : undefined,
        })
        // The question has gone whatever happens next; a picture that fails is
        // reported as such, never as a failed question (supportMedia.ts).
        const { failed } = await attachSupportPictures(id, pictures)
        clearPictures()
        // 123. The lane reads in its own language: ask for the rendering now,
        // without waiting — the desk asks again on open if this did not land.
        void requestSupportTranslation(id)
        setOutcome({ kind: 'lane', lane, picturesFailed: failed })
      } else {
        setOutcome({ kind: 'board', post: await postQuestion(question, detail || undefined) })
      }
      // 126. Not awaited and never throws: a stamp that fails costs one line
      // on the Ask One tab, and must never cost somebody their question.
      void stampAskOne(logRefRef.current, { outcome: lane ?? 'board' })
      setPhase('sent')
    } catch (e) {
      setError(messageOf(e))
    } finally {
      setPosting(false)
    }
  }

  function cancel() {
    setPhase('idle')
    setError(null)
  }

  function reset() {
    setPhase('idle')
    setQuestion('')
    setDetail('')
    setOneSummary('')
    setRouteKey(null)
    setPriority(null)
    setOutcome(null)
    setError(null)
    clearPictures()
    setPageContext(null)
    logRefRef.current = null
  }

  return {
    phase, question, setQuestion, detail, setDetail, routeKey, setRouteKey, priority, setPriority,
    posting, error, outcome, openBoard, start, post, sendFeedback, cancel, reset, media,
  }
}

/**
 * What the person is told after a send. ONE card for both surfaces, because
 * the promise it makes ("you will get a notification", "a moderator will look
 * at it") is a claim about the system and must not be phrased twice.
 *
 * The board has THREE outcomes since 096 and each says something different
 * and true: it is up; it was held back and here is the check's own sentence
 * about why; or the check could not be reached, so a curator has it. The
 * second is David's "they get a notice saying, sorry, your thing was
 * moderated" — in the same breath, not a bell later.
 */
export function RouteSent({
  outcome,
  onDone,
  doneLabel,
}: {
  outcome: RouteOutcome
  onDone: () => void
  /** 337. Help's dialogs close on Done; the drawer's default asks again. */
  doneLabel?: string
}) {
  const lang = useHubLang()
  const c = ASK[lang]
  let head: string
  let body: ReactNode
  if (outcome.kind === 'feedback') {
    head = c.sentFeedbackHead
    body = c.sentFeedbackBody
  } else if (outcome.kind === 'lane') {
    head = c.sentTo(laneWords(outcome.lane, lang))
    body = outcome.picturesFailed
      ? `${c.sentLaneBody} ${c.sentPicturesFailed(outcome.picturesFailed)}`
      : c.sentLaneBody
  } else if (outcome.post.state === 'live') {
    head = c.liveHead
    body = c.liveBody
  } else if (outcome.post.state === 'blocked') {
    head = c.blockedHead
    body = c.blockedBody(outcome.post.reason)
  } else {
    head = c.heldHead
    body = c.heldBody
  }
  return (
    <div className="oneroute">
      <p className="oneroute__head">{head}</p>
      <p className="onemsg__body">{body}</p>
      <div className="oneroute__actions">
        <button className="btn btn--quiet btn--sm" type="button" onClick={onDone}>
          {doneLabel ?? c.askSomethingElse}
        </button>
      </div>
    </div>
  )
}

/**
 * WHAT TO SAY WHEN THERE IS NO DOOR — HLP-22, and it has ONE home.
 *
 * Rendered where a person would otherwise look for the HR door - Help's
 * ticket form, since S2 - so that "there is no route" is a thing the person
 * READS rather than a thing they find out by pressing something. One home, so
 * there are not two copies of it to drift.
 */
export function NoPrivateHrRoute() {
  const c = ASK[useHubLang()]
  return (
    <p className="oneroute__warn">
      <Emph parts={c.noHrRoute} />
    </p>
  )
}

/**
 * THE BOARD DOOR, AND ONLY IT — #/wiki's "Post to the board" (S2, 31 Oct).
 *
 * What is left of the old routing step. It used to open after a thumbs-down
 * on any surface and list every destination; v2 moved the private ones to
 * Help (where the person picks), so the only door still opened from an answer
 * is the public one, and only from the page whose job it is. 034's warning is
 * here, read at the moment of choosing, with the exact text that will publish
 * on screen and editable - that is RouteQuestionFields with no lane.
 */
export function BoardStep({
  question,
  setQuestion,
  detail,
  setDetail,
  onSend,
  onCancel,
  busy,
}: {
  question: string
  setQuestion: (v: string) => void
  detail: string
  setDetail: (v: string) => void
  onSend: () => void
  onCancel: () => void
  busy: boolean
}) {
  const c = ASK[useHubLang()]
  return (
    <div className="oneroute">
      <p className="oneroute__head">{c.communityName}</p>
      <p className="onemsg__body">{c.communityWho}</p>
      <RouteQuestionFields
        question={question}
        setQuestion={setQuestion}
        detail={detail}
        setDetail={setDetail}
        toLane={null}
      />
      <div className="oneroute__actions">
        <button
          className="btn btn--primary btn--sm"
          type="button"
          onClick={onSend}
          disabled={busy || !question.trim()}
        >
          {busy ? c.sending : c.postToCommunity}
        </button>
        <button className="btn btn--quiet" type="button" onClick={onCancel}>
          {c.notNow}
        </button>
      </div>
    </div>
  )
}
